import { type ReactElement, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  UpdateFollowsData,
  UpdateFollowsVariables,
} from '../views/following/graphql/updateFollows';
import { UPDATE_FOLLOWS } from '../views/following/graphql/updateFollows';
import { useMutation } from '@apollo/client';
import { useApp } from './AppProvider';
import { useFollowing } from './FollowingProvider';

const FollowingStateSynchronizer = (): ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [{ event, hasRacesWithPredictiveTracking }] = useApp();
  const { id: eventId } = event || {};
  const [{ status, following, focussed }, dispatch] = useFollowing();

  const [updateFollows] = useMutation<UpdateFollowsData, UpdateFollowsVariables>(UPDATE_FOLLOWS);

  const dirtySet = useMemo(() => {
    const fromUrl = new Set(searchParams.getAll('follows'));
    const fromState = new Set(following.map((follow) => follow.resultCode));
    return fromUrl.symmetricDifference(fromState);
  }, [searchParams, following]);

  useEffect(() => {
    const fromUrl = new Set(searchParams.getAll('follows'));
    const currentActive = searchParams.get('active');
    if (
      eventId &&
      hasRacesWithPredictiveTracking &&
      status === 'initialized' &&
      dirtySet.size > 0
    ) {
      updateFollows({ variables: { uniqueCodes: Array.from(fromUrl), eventId } }).then(
        ({ data }) => {
          const newFollows = data?.updateFollows.data || [];
          dispatch({ type: 'setFollows', payload: newFollows });
          if (!currentActive && newFollows.length > 0) {
            const newActive = newFollows[0].resultCode;
            searchParams.set('active', newActive);
            dispatch({ type: 'focus', payload: newActive });
            setSearchParams(searchParams);
          }
        }
      );
    }
  }, [
    eventId,
    hasRacesWithPredictiveTracking,
    updateFollows,
    status,
    searchParams,
    dispatch,
    dirtySet,
    setSearchParams,
    focussed?.resultCode,
    following,
  ]);

  useEffect(() => {
    const currentActive = searchParams.get('active');
    if (following.length > 0 && focussed?.resultCode !== currentActive) {
      dispatch({ type: 'focus', payload: currentActive ?? following[0].resultCode });
    }
  }, [dispatch, focussed?.resultCode, following, searchParams, setSearchParams]);

  return <></>;
};

export default FollowingStateSynchronizer;
